import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { CalendarClock, Check, Clock3, X } from 'lucide-react';
import { toast } from 'react-toastify';
import ConfirmModal from '../ConfirmModal.tsx';
import {
  createAgendaAppointment,
  fetchAgendaAvailability,
  updateAgendaAppointment,
  updateAgendaAppointmentStatus,
} from '../../services/api.ts';
import type {
  AgendaAppointment,
  AgendaAppointmentStatus,
  AgendaProfessional,
  AgendaService,
  AvailabilitySlot,
} from '../../types';

type AppointmentModalProps = {
  open: boolean;
  appointment: AgendaAppointment | null;
  initialStart: Date | null;
  timezone: string;
  services: AgendaService[];
  professionals: AgendaProfessional[];
  onClose: () => void;
  onSaved: (appointment: AgendaAppointment) => void;
};

const FINAL_STATUSES = new Set<AgendaAppointmentStatus>(['completed', 'cancelled', 'no_show']);

const statusLabels: Record<AgendaAppointmentStatus, string> = {
  scheduled: 'Agendada',
  confirmed: 'Confirmada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  no_show: 'Não compareceu',
};

const formatLocalInput = (value: string | Date, timezone: string) =>
  formatInTimeZone(value, timezone, "yyyy-MM-dd'T'HH:mm");

const AppointmentModal = ({
  open,
  appointment,
  initialStart,
  timezone,
  services,
  professionals,
  onClose,
  onSaved,
}: AppointmentModalProps) => {
  const idempotencyKey = useRef(
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `agenda-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  );
  const [serviceId, setServiceId] = useState('');
  const [professionalId, setProfessionalId] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [startLocal, setStartLocal] = useState('');
  const [notes, setNotes] = useState('');
  const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pendingStatus, setPendingStatus] = useState<AgendaAppointmentStatus | null>(null);

  const selectableServices = useMemo(() => services.filter((service) => (
    service.active || service.id === appointment?.serviceId
  )), [appointment?.serviceId, services]);
  const compatibleProfessionals = useMemo(() => professionals.filter((professional) => {
    const isCurrentSelection = professional.id === appointment?.professionalId
      && serviceId === appointment.serviceId;
    return isCurrentSelection || (
      professional.active && professional.serviceIds.includes(serviceId)
    );
  }), [appointment, professionals, serviceId]);
  const professionalFilterIsBookable = useMemo(() => professionals.some((professional) => (
    professional.id === professionalId
    && professional.active
    && professional.serviceIds.includes(serviceId)
  )), [professionalId, professionals, serviceId]);
  const requestedStartAt = useMemo(() => {
    if (!startLocal) return '';
    try {
      return fromZonedTime(startLocal, timezone).toISOString();
    } catch {
      return '';
    }
  }, [startLocal, timezone]);
  const selectedSlotIsAvailable = useMemo(() => slots.some((slot) => (
    slot.startAt === requestedStartAt
    && (!professionalId || slot.professionalId === professionalId)
  )), [professionalId, requestedStartAt, slots]);
  const isFinal = appointment ? FINAL_STATUSES.has(appointment.status) : false;

  useEffect(() => {
    if (!open) return;
    const fallbackServiceId = selectableServices[0]?.id || '';
    setServiceId(appointment?.serviceId || fallbackServiceId);
    setProfessionalId(appointment?.professionalId || '');
    setClientName(appointment?.client.name || '');
    setClientPhone(appointment?.client.phone || '');
    setClientEmail(appointment?.client.email || '');
    setStartLocal(
      appointment?.startAt
        ? formatLocalInput(appointment.startAt, timezone)
        : initialStart
          ? formatLocalInput(initialStart, timezone)
          : formatLocalInput(new Date(), timezone),
    );
    setNotes(appointment?.notes || '');
    setSlots([]);
    setError('');
    setPendingStatus(null);
    idempotencyKey.current =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `agenda-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }, [appointment, initialStart, open, selectableServices, timezone]);

  useEffect(() => {
    if (!open || !serviceId || !startLocal.slice(0, 10)) return undefined;
    let cancelled = false;
    setLoadingSlots(true);
    fetchAgendaAvailability({
      date: startLocal.slice(0, 10),
      serviceId,
      ...(professionalId && professionalFilterIsBookable ? { professionalId } : {}),
      ...(appointment ? { appointmentId: appointment.id } : {}),
    })
      .then((response) => {
        if (!cancelled) setSlots(response.slots);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appointment, open, professionalFilterIsBookable, professionalId, serviceId, startLocal]);

  if (!open) return null;

  const handleServiceChange = (nextServiceId: string) => {
    setServiceId(nextServiceId);
    const selectedProfessional = professionals.find((item) => item.id === professionalId);
    if (selectedProfessional && !selectedProfessional.serviceIds.includes(nextServiceId)) {
      setProfessionalId('');
    }
  };

  const selectSlot = (slot: AvailabilitySlot) => {
    setStartLocal(formatLocalInput(slot.startAt, timezone));
    setProfessionalId(slot.professionalId);
    setError('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isFinal || saving) return;
    if (loadingSlots) {
      setError('Aguarde a atualização dos horários disponíveis.');
      return;
    }
    if (!selectedSlotIsAvailable) {
      setError('Selecione um dos horários disponíveis abaixo. A agenda vazia não significa que o profissional atende em qualquer horário.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        serviceId,
        professionalId: professionalId || null,
        client: {
          name: clientName,
          phone: clientPhone || null,
          email: clientEmail || null,
        },
        startAt: requestedStartAt,
        notes: notes || null,
      };
      const saved = appointment
        ? await updateAgendaAppointment(appointment.id, payload)
        : await createAgendaAppointment({
          ...payload,
          idempotencyKey: idempotencyKey.current,
        });
      toast.success(appointment ? 'Agendamento atualizado.' : 'Agendamento criado.');
      onSaved(saved);
    } catch (requestError: any) {
      const message = requestError?.response?.data?.error || 'Não foi possível salvar o agendamento.';
      setError(message);
      const alternatives = requestError?.response?.data?.details?.alternatives;
      if (Array.isArray(alternatives)) setSlots(alternatives);
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (status: AgendaAppointmentStatus) => {
    if (!appointment || saving) return;
    setSaving(true);
    setError('');
    try {
      const saved = await updateAgendaAppointmentStatus(appointment.id, status);
      toast.success(`Agendamento marcado como ${statusLabels[status].toLowerCase()}.`);
      setPendingStatus(null);
      onSaved(saved);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Não foi possível atualizar o status.');
    } finally {
      setSaving(false);
    }
  };

  const pendingStatusCopy = pendingStatus ? {
    completed: {
      title: 'Concluir agendamento?',
      description: 'O agendamento ficará somente para leitura e não poderá ser reaberto nesta versão.',
      confirmLabel: 'Concluir',
      variant: 'primary' as const,
    },
    cancelled: {
      title: 'Cancelar agendamento?',
      description: 'O horário será liberado e o agendamento não poderá ser reaberto nesta versão.',
      confirmLabel: 'Cancelar agendamento',
      variant: 'destructive' as const,
    },
    no_show: {
      title: 'Marcar não comparecimento?',
      description: 'O agendamento ficará em estado final e não poderá ser reaberto nesta versão.',
      confirmLabel: 'Marcar não comparecimento',
      variant: 'destructive' as const,
    },
  }[pendingStatus as 'completed' | 'cancelled' | 'no_show'] : null;

  return (
    <>
      <div className="agenda-modal-backdrop" role="presentation">
        <section className="agenda-modal" role="dialog" aria-modal="true" aria-labelledby="appointment-title">
        <header className="agenda-modal-header">
          <div>
            <span className="agenda-eyebrow"><CalendarClock size={15} /> Agendamento</span>
            <h2 id="appointment-title">{appointment ? 'Detalhes do agendamento' : 'Novo agendamento'}</h2>
            {appointment && (
              <span className={`agenda-status status-${appointment.status}`}>
                {statusLabels[appointment.status]}
              </span>
            )}
          </div>
          <button type="button" className="agenda-icon-button" onClick={onClose} aria-label="Fechar">
            <X size={20} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="agenda-form">
          {error && <div className="agenda-alert agenda-alert-error">{error}</div>}
          {isFinal && (
            <div className="agenda-alert agenda-alert-info">
              Este agendamento está em um estado final e permanece disponível somente para leitura.
            </div>
          )}

          <div className="agenda-form-grid">
            <label>
              <span>Serviço *</span>
              <select value={serviceId} onChange={(event) => handleServiceChange(event.target.value)} disabled={isFinal} required>
                <option value="">Selecione</option>
                {selectableServices.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} · {service.durationMinutes} min{service.active ? '' : ' · inativo'}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Profissional</span>
              <select value={professionalId} onChange={(event) => setProfessionalId(event.target.value)} disabled={isFinal}>
                <option value="">Qualquer profissional disponível</option>
                {compatibleProfessionals.map((professional) => (
                  <option
                    key={professional.id}
                    value={professional.id}
                    disabled={!professional.active || !professional.serviceIds.includes(serviceId)}
                  >
                    {professional.name}
                    {!professional.active || !professional.serviceIds.includes(serviceId) ? ' · indisponível para novos horários' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Início *</span>
              <input
                type="datetime-local"
                value={startLocal}
                onChange={(event) => setStartLocal(event.target.value)}
                disabled={isFinal}
                required
              />
            </label>
            <label>
              <span>Nome do cliente *</span>
              <input value={clientName} onChange={(event) => setClientName(event.target.value)} maxLength={150} disabled={isFinal} required />
            </label>
            <label>
              <span>Telefone</span>
              <input type="tel" value={clientPhone} onChange={(event) => setClientPhone(event.target.value)} maxLength={30} disabled={isFinal} placeholder="Opcional" />
            </label>
            <label>
              <span>E-mail</span>
              <input type="email" value={clientEmail} onChange={(event) => setClientEmail(event.target.value)} maxLength={254} disabled={isFinal} placeholder="Opcional" />
            </label>
          </div>

          <label>
            <span>Observações operacionais</span>
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} maxLength={1000} rows={3} disabled={isFinal} />
            <small>Esta agenda não é prontuário. Não registre diagnósticos ou informações clínicas sensíveis.</small>
          </label>

          {!isFinal && (
            <div className="agenda-slot-box">
              <div className="agenda-slot-title">
                <Clock3 size={16} />
                <strong>Horários disponíveis no dia</strong>
                {loadingSlots && <span>Atualizando…</span>}
              </div>
              {!loadingSlots && slots.length === 0 ? (
                <p>Nenhum horário disponível para os filtros selecionados.</p>
              ) : (
                <div className="agenda-slot-list">
                  {slots.slice(0, 12).map((slot) => (
                    <button key={`${slot.professionalId}-${slot.startAt}`} type="button" onClick={() => selectSlot(slot)}>
                      <Clock3 size={13} />
                      {formatInTimeZone(slot.startAt, timezone, 'HH:mm')}
                      <span>{slot.professionalName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <footer className="agenda-modal-footer">
            {appointment && !isFinal && (
              <div className="agenda-status-actions">
                {appointment.status === 'scheduled' && (
                  <button type="button" className="agenda-button secondary" onClick={() => handleStatus('confirmed')} disabled={saving}>
                    <Check size={16} /> Confirmar
                  </button>
                )}
                <button type="button" className="agenda-button secondary" onClick={() => setPendingStatus('completed')} disabled={saving}>
                  Concluir
                </button>
                <button type="button" className="agenda-button secondary" onClick={() => setPendingStatus('no_show')} disabled={saving}>
                  Não compareceu
                </button>
                <button type="button" className="agenda-button danger" onClick={() => setPendingStatus('cancelled')} disabled={saving}>
                  Cancelar agendamento
                </button>
              </div>
            )}
            <div className="agenda-footer-main-actions">
              <button type="button" className="agenda-button ghost" onClick={onClose}>Fechar</button>
              {!isFinal && (
                <button type="submit" className="agenda-button primary" disabled={saving || loadingSlots || !serviceId || !clientName || !startLocal}>
                  {saving ? 'Salvando…' : appointment ? 'Salvar alterações' : 'Criar agendamento'}
                </button>
              )}
            </div>
          </footer>
        </form>
        </section>
      </div>
      <ConfirmModal
        open={Boolean(pendingStatusCopy)}
        title={pendingStatusCopy?.title || ''}
        description={pendingStatusCopy?.description}
        confirmLabel={pendingStatusCopy?.confirmLabel}
        variant={pendingStatusCopy?.variant}
        saving={saving}
        onCancel={() => setPendingStatus(null)}
        onConfirm={() => pendingStatus && void handleStatus(pendingStatus)}
      />
    </>
  );
};

export default AppointmentModal;
